import { useMemo } from "react";
import type { CatalogItem } from "@apex/shared";
import { cn } from "@apex/shared";
import { useWidget } from "../../state";
import { PersistentSummaryPanel } from "../PersistentSummaryPanel";
import { QuoteForm } from "../QuoteForm";

export interface SummaryViewProps {
  className?: string;
}

/** Known category name for the base vehicle item */
const VEHICLE_CATEGORY = "Vehicle";

/** Spec keys to hide (internal/price-related) */
const HIDDEN_SPEC_KEYS = new Set([
  "baseprice", "price", "msrp", "currency", "basevehicle", "vehiclemake", "vehicletype",
]);

/** Priority order for spec display */
const SPEC_PRIORITY: string[] = [
  "workingHeight", "workingOutreach", "workingLoad", "baseWeight",
];

const SPEC_LABELS: Record<string, string> = {
  workingHeight: "Height",
  workingOutreach: "Outreach",
  workingLoad: "Max Load",
  baseWeight: "Weight",
  boomRotation: "Rotation",
  basket: "Basket",
  engineType: "Engine",
};

/** Get the best display name for an item */
function getItemDisplayName(item: CatalogItem): string {
  return item.name || item.code;
}

/** Chevron-left icon */
function ChevronLeft() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

/** Pen icon for Edit button */
function PenIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    </svg>
  );
}

export function SummaryView({ className }: SummaryViewProps) {
  const { state, selectedItemList, goBack } = useWidget();
  const { selectedRootItem, categories } = state;

  if (!selectedRootItem) {
    return null;
  }

  const images = selectedRootItem.images || [];
  const mainImage = images[0];

  // Extract top specs for hero display
  const heroSpecs = useMemo(() => {
    if (!selectedRootItem?.metadata) return [];
    const entries = Object.entries(selectedRootItem.metadata);
    const visible = entries.filter(
      ([key]) => !HIDDEN_SPEC_KEYS.has(key.toLowerCase())
    );
    visible.sort((a, b) => {
      const ai = SPEC_PRIORITY.indexOf(a[0]);
      const bi = SPEC_PRIORITY.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
    return visible.slice(0, 4);
  }, [selectedRootItem]);

  // Separate vehicle items from other items (still useful for hero copy)
  const { vehicleItem } = useMemo(() => {
    const vehicleCategoryId = categories.find(
      (c) => c.name === VEHICLE_CATEGORY
    )?.id;

    let vehicle: CatalogItem | null = null;

    for (const item of selectedItemList) {
      const catId = item.categoryId != null
        ? String(item.categoryId)
        : null;
      const catName = item.categoryName || "";

      if (
        (vehicleCategoryId && catId === String(vehicleCategoryId)) ||
        catName === VEHICLE_CATEGORY
      ) {
        if (!vehicle) vehicle = item;
      }
    }

    return { vehicleItem: vehicle };
  }, [selectedItemList, categories]);

  return (
    <div className={cn("apex-flex apex-flex-col apex-h-full", className)}>
      {/* Header bar */}
      <div className="apex-flex apex-items-center apex-gap-3 apex-px-6 apex-py-2 apex-border-b apex-border-border apex-bg-background apex-shrink-0">
        <button
          type="button"
          onClick={goBack}
          className="apex-shrink-0 apex-inline-flex apex-items-center apex-gap-1 apex-min-h-[44px] apex-px-2 apex-py-1.5 apex-rounded-md hover:apex-bg-muted apex-text-muted-foreground hover:apex-text-foreground apex-transition-colors apex-text-sm apex-font-medium"
          aria-label="Edit configuration"
        >
          <ChevronLeft />
          <span>Edit configuration</span>
        </button>
        <h2 className="apex-font-semibold apex-text-foreground apex-truncate apex-font-display">
          Review Configuration
        </h2>
      </div>

      {/* Two-column body */}
      <div className="apex-flex apex-flex-col lg:apex-flex-row apex-flex-1 apex-min-h-0">

        {/* Left: Configuration Summary */}
        <div className="apex-flex-1 apex-overflow-auto apex-min-h-0">

          {/* HERO: Full-width product showcase */}
          <div
            className="apex-relative apex-animate-fade-in"
            style={{ background: "var(--apex-gradient-surface)" }}
          >
            {/* Product image */}
            {mainImage ? (
              <div
                className="apex-flex apex-items-center apex-justify-center apex-px-8 apex-pt-6 apex-pb-3"
                style={{ minHeight: "200px" }}
              >
                <img
                  src={mainImage}
                  alt={selectedRootItem.name || selectedRootItem.code}
                  className="apex-object-contain"
                  style={{ maxHeight: "180px", maxWidth: "100%" }}
                />
              </div>
            ) : (
              <div style={{ height: "60px" }} />
            )}

            {/* Item info bar */}
            <div className="apex-px-5 apex-pb-4 apex-pt-1">
              <div className="apex-flex apex-items-start apex-justify-between apex-gap-3">
                <div className="apex-min-w-0">
                  <h3 className="apex-text-lg apex-font-bold apex-text-foreground apex-font-display apex-leading-tight">
                    {selectedRootItem.name || selectedRootItem.code}
                  </h3>
                  <div className="apex-flex apex-items-center apex-gap-2 apex-mt-0.5">
                    {selectedRootItem.name && selectedRootItem.name !== selectedRootItem.code && (
                      <span className="apex-text-xs apex-text-muted-foreground apex-font-mono">
                        {selectedRootItem.code}
                      </span>
                    )}
                    {vehicleItem && (
                      <>
                        {selectedRootItem.name && selectedRootItem.name !== selectedRootItem.code && (
                          <span className="apex-text-muted-foreground/40">|</span>
                        )}
                        <span className="apex-text-xs apex-text-muted-foreground">
                          {getItemDisplayName(vehicleItem)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={goBack}
                  className="apex-shrink-0 apex-flex apex-items-center apex-gap-1.5 apex-text-sm apex-text-primary hover:apex-text-primary/80 apex-font-medium apex-transition-colors apex-px-3 apex-py-1.5 apex-rounded-lg hover:apex-bg-primary/5 apex-border apex-border-primary/20"
                >
                  <PenIcon />
                  Edit
                </button>
              </div>

              {/* Spec grid */}
              {heroSpecs.length > 0 && (
                <div
                  // Two columns on phones, up to four from sm. `minmax(0, 1fr)`
                  // rather than `1fr`: a bare 1fr track can't shrink below its
                  // content, which pushed the grid past the host at 360px.
                  className={cn(
                    "apex-grid apex-gap-2 apex-mt-3 apex-grid-cols-2",
                    heroSpecs.length >= 4 ? "sm:apex-grid-cols-4" : heroSpecs.length === 3 ? "sm:apex-grid-cols-3" : "",
                  )}
                >
                  {heroSpecs.map(([key, value]) => (
                    <div
                      key={key}
                      className="apex-rounded-lg apex-bg-background/70 apex-border apex-border-border/50 apex-px-3 apex-py-2 apex-text-center"
                    >
                      <div className="apex-text-xs apex-text-muted-foreground apex-mb-0.5">
                        {SPEC_LABELS[key] || key}
                      </div>
                      <div className="apex-text-sm apex-font-bold apex-text-foreground apex-tabular-nums">
                        {String(value)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/*
            Per #540 the standalone Step-3 SummaryView no longer duplicates
            the items / breakdown UI — that lives in PersistentSummaryPanel
            on the right. This left column is the QuoteForm.
          */}
          <div className="apex-px-5 apex-py-4">
            <QuoteForm />
          </div>
        </div>

        {/* Right: PersistentSummaryPanel (#540) replaces the legacy breakdown */}
        <PersistentSummaryPanel />
      </div>
    </div>
  );
}
